import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Star,
  ThumbsUp,
  Flag,
  MessageSquare,
  User,
  Calendar,
  Award,
  TrendingUp,
  Filter,
  Plus,
  X,
  Send,
  CheckCircle,
  AlertCircle,
  LogIn,
  UserPlus
} from 'lucide-react';

interface Review {
  _id: string;
  user: {
    _id: string;
    firstName: string;
    lastName: string;
    profileImage?: string;
    role: string;
  };
  rating: number;
  title: string;
  content: string;
  projectType: string;
  helpfulCount: number;
  helpfulBy?: string[];
  createdAt: string;
  featured: boolean;
}

interface ReviewStats {
  totalReviews: number;
  averageRating: number;
  ratingDistribution: { [key: number]: number };
  recentReviewsCount: number;
}

interface ReviewFormData {
  rating: number;
  title: string;
  content: string;
  projectType: string;
}

interface EligibilityData {
  eligible: boolean;
  reason?: string;
}

const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};

const API_BASE_URL = getApiBaseUrl();

const ReviewsSection: React.FC = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [stats, setStats] = useState<ReviewStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [ratingFilter, setRatingFilter] = useState<number | null>(null);
  const [eligibilityData, setEligibilityData] = useState<EligibilityData | null>(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);

  const [formData, setFormData] = useState<ReviewFormData>({
    rating: 5,
    title: '',
    content: '',
    projectType: ''
  });

  const projectTypes = [
    'Web Development',
    'Mobile App Development',
    'Backend Development',
    'UI/UX Design',
    'AI/Machine Learning',
    'DevOps & Cloud',
    'E-commerce',
    'Custom Software',
    'Consulting',
    'Other'
  ];

  // Fetch reviews and stats
  useEffect(() => {
    fetchReviews();
    fetchStats();
  }, [currentPage, ratingFilter]);

  // Check eligibility when user is authenticated
  useEffect(() => {
    if (user) {
      checkEligibility();
    }
  }, [user]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '6'
      });

      if (ratingFilter) {
        params.append('rating', ratingFilter.toString());
      }

      const response = await fetch(`${API_BASE_URL}/reviews`);
      const data = await response.json();      if (data.success) {
        setReviews(data.data.reviews);
        setTotalPages(data.data.pagination.totalPages);
      }
    } catch (error) {    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/reviews/stats`);
      const data = await response.json();

      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {    }
  };

  const checkEligibility = async () => {
    if (!user) return;

    setCheckingEligibility(true);
    try {
      const response = await fetch(`${API_BASE_URL}/reviews/eligibility`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setEligibilityData(data.data);
      }
    } catch (error) {    } finally {
      setCheckingEligibility(false);
    }
  };

  const handleWriteReviewClick = () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (eligibilityData?.eligible) {
      setShowReviewForm(true);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRatingChange = (rating: number) => {
    setFormData(prev => ({
      ...prev,
      rating
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.title.trim() || formData.title.length < 5) {
      setErrorMessage('Title must be at least 5 characters long');
      return false;
    }
    if (!formData.content.trim() || formData.content.length < 20) {
      setErrorMessage('Review content must be at least 20 characters long');
      return false;
    }
    return true;
  };

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!validateForm()) {
      setSubmitStatus('error');
      return;
    }

    setSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch(`${API_BASE_URL}/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          rating: 5,
          title: '',
          content: '',
          projectType: ''
        });
        setShowReviewForm(false);
        // Refresh reviews and stats
        fetchReviews();
        fetchStats();
      } else {
        throw new Error(data.message || 'Failed to submit review');
      }
    } catch (error: any) {      setSubmitStatus('error');
      setErrorMessage(error.message || 'Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkHelpful = async (reviewId: string) => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/reviews/${reviewId}/helpful`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (response.ok) {
        // Update the review in the local state
        setReviews(prev => prev.map(review => 
          review._id === reviewId 
            ? { ...review, helpfulCount: review.helpfulCount + 1 }
            : review
        ));
      }
    } catch (error) {    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return '1 day ago';
    if (diffDays < 30) return `${diffDays} days ago`;
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return months === 1 ? '1 month ago' : `${months} months ago`;
    }
    const years = Math.floor(diffDays / 365);
    return years === 1 ? '1 year ago' : `${years} years ago`;
  };

  const renderStars = (rating: number, size: 'sm' | 'md' | 'lg' = 'md') => {
    const sizeClasses = {
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-6 h-6'
    };

    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${sizeClasses[size]} ${
              star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const renderRatingDistribution = () => {
    if (!stats) return null;

    return (
      <div className="space-y-2">
        {[5, 4, 3, 2, 1].map((rating) => {
          const count = stats.ratingDistribution[rating] || 0;
          const percentage = stats.totalReviews > 0 ? (count / stats.totalReviews) * 100 : 0;

          return (
            <div key={rating} className="flex items-center space-x-3">
              <span className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} w-8`}>
                {rating}★
              </span>
              <div className={`flex-1 h-2 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded-full overflow-hidden`}>
                <div
                  className="h-full bg-yellow-400 transition-all duration-300"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} w-8 text-right`}>
                {count}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderWriteReviewButton = () => {
    if (!user) {
      return (
        <div className="mt-6 pt-6 border-t border-gray-300 dark:border-gray-600">
          <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'} border mb-4`}>
            <p className={`text-sm ${theme === 'dark' ? 'text-blue-300' : 'text-blue-800'} mb-3`}>
              Sign in to write a review and share your experience
            </p>
            <div className="flex flex-col space-y-2">
              <button
                onClick={() => navigate('/login')}
                className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Sign In
              </button>
              <button
                onClick={() => navigate('/register')}
                className={`inline-flex items-center justify-center px-4 py-2 ${theme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} border rounded-lg transition-colors text-sm font-medium`}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Create Account
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (checkingEligibility) {
      return (
        <div className="mt-6 pt-6 border-t border-gray-300 dark:border-gray-600">
          <div className="animate-pulse">
            <div className={`h-10 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded-lg`}></div>
          </div>
        </div>
      );
    }

    if (eligibilityData?.eligible) {
      return (
        <div className="mt-6 pt-6 border-t border-gray-300 dark:border-gray-600">
          <button
            onClick={handleWriteReviewClick}
            className="w-full inline-flex items-center justify-center px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Write a Review
          </button>
        </div>
      );
    }

    if (eligibilityData && !eligibilityData.eligible) {
      return (
        <div className="mt-6 pt-6 border-t border-gray-300 dark:border-gray-600">
          <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'} border`}>
            <div className="flex items-start">
              <AlertCircle className={`w-5 h-5 ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'} mr-2 mt-0.5 flex-shrink-0`} />
              <div>
                <p className={`text-sm ${theme === 'dark' ? 'text-yellow-300' : 'text-yellow-800'} font-medium mb-1`}>
                  Review Submission Restricted
                </p>
                <p className={`text-sm ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-700'}`}>
                  {eligibilityData.reason}
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <section className={`py-20 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className={`text-3xl md:text-4xl font-bold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} mb-4`}>
            What Our Users Say
          </h2>
          <p className={`text-xl ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} max-w-3xl mx-auto mb-8`}>
            Real reviews from real users who have experienced our platform
          </p>

          {/* Stats Overview */}
          {stats && stats.totalReviews > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} border`}>
                <div className="flex items-center justify-center mb-2">
                  <Star className="w-8 h-8 text-yellow-400 fill-current" />
                </div>
                <div className={`text-3xl font-bold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} mb-1`}>
                  {stats.averageRating.toFixed(1)}
                </div>
                <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Average Rating
                </div>
              </div>

              <div className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} border`}>
                <div className="flex items-center justify-center mb-2">
                  <MessageSquare className="w-8 h-8 text-blue-500" />
                </div>
                <div className={`text-3xl font-bold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} mb-1`}>
                  {stats.totalReviews}
                </div>
                <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Total Reviews
                </div>
              </div>

              <div className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} border`}>
                <div className="flex items-center justify-center mb-2">
                  <TrendingUp className="w-8 h-8 text-green-500" />
                </div>
                <div className={`text-3xl font-bold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} mb-1`}>
                  {stats.recentReviewsCount}
                </div>
                <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  This Month
                </div>
              </div>

              <div className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} border`}>
                <div className="flex items-center justify-center mb-2">
                  <Award className="w-8 h-8 text-purple-500" />
                </div>
                <div className={`text-3xl font-bold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} mb-1`}>
                  {stats.totalReviews > 0 ? Math.round((stats.ratingDistribution[5] / stats.totalReviews) * 100) : 0}%
                </div>
                <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  5-Star Reviews
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Rating Distribution Sidebar */}
          <div className="lg:col-span-1">
            <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} rounded-xl border p-6 sticky top-6`}>
              {stats && stats.totalReviews > 0 ? (
                <>
                  <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} mb-4`}>
                    Rating Distribution
                  </h3>
                  {renderRatingDistribution()}

                  {/* Filters */}
                  <div className="mt-6 pt-6 border-t border-gray-300 dark:border-gray-600">
                    <h4 className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
                      Filter by Rating
                    </h4>
                    <div className="space-y-2">
                      <button
                        onClick={() => setRatingFilter(null)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                          ratingFilter === null
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        All Reviews
                      </button>
                      {[5, 4, 3, 2, 1].map((rating) => (
                        <button
                          key={rating}
                          onClick={() => setRatingFilter(rating)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between ${
                            ratingFilter === rating
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                              : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          <span className="flex items-center">
                            {renderStars(rating, 'sm')}
                          </span>
                          <span>{stats?.ratingDistribution[rating] || 0}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <MessageSquare className={`w-12 h-12 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'} mx-auto mb-3`} />
                  <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-2`}>
                    No Reviews Yet
                  </h3>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Be the first to share your experience!
                  </p>
                </div>
              )}

              {/* Write Review Button */}
              {renderWriteReviewButton()}
            </div>
          </div>

          {/* Reviews List */}
          <div className="lg:col-span-3">
            {/* Success Message */}
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  <span className="text-green-800 font-medium">
                    Review submitted successfully! It will be visible after approval.
                  </span>
                </div>
              </div>
            )}

            {loading ? (
              <div className="space-y-6">
                {[...Array(3)].map((_, index) => (
                  <div key={index} className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'} rounded-xl p-6 animate-pulse`}>
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                      <div className="flex-1 space-y-3">
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/4"></div>
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
                        <div className="h-20 bg-gray-300 dark:bg-gray-600 rounded"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-6">
                {reviews.map((review) => (
                  <div key={review._id} className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border p-6 hover:shadow-lg transition-shadow`}>
                    <div className="flex items-start space-x-4">
                      {/* User Avatar */}
                      <div className="flex-shrink-0">
                        {review.user.profileImage ? (
                          <img
                            src={review.user.profileImage}
                            alt={`${review.user.firstName} ${review.user.lastName}`}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className={`w-12 h-12 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} rounded-full flex items-center justify-center`}>
                            <User className={`w-6 h-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                          </div>
                        )}
                      </div>

                      {/* Review Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className={`font-semibold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
                              {review.user.firstName} {review.user.lastName}
                              {review.featured && (
                                <Award className="inline w-4 h-4 text-yellow-500 ml-2" />
                              )}
                            </h4>
                            <div className="flex items-center space-x-2 mt-1">
                              {renderStars(review.rating, 'sm')}
                              <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                {formatDate(review.createdAt)}
                              </span>
                              {review.projectType && (
                                <span className={`px-2 py-1 text-xs ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'} rounded-full`}>
                                  {review.projectType}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <h5 className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'} mb-2`}>
                          {review.title}
                        </h5>

                        <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} leading-relaxed mb-4`}>
                          {review.content}
                        </p>

                        {/* Review Actions */}
                        <div className="flex items-center space-x-4">
                          <button
                            onClick={() => handleMarkHelpful(review._id)}
                            disabled={!user || review.helpfulBy?.includes(user?._id || '')}
                            className={`inline-flex items-center space-x-2 px-3 py-1 rounded-lg text-sm transition-colors ${
                              review.helpfulBy?.includes(user?._id || '')
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
                            } ${!user ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <ThumbsUp className="w-4 h-4" />
                            <span>Helpful ({review.helpfulCount})</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center space-x-2 mt-8">
                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentPage(index + 1)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === index + 1
                            ? 'bg-blue-600 text-white'
                            : `${theme === 'dark' ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`
                        }`}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </div>
                )}

                {reviews.length === 0 && !loading && (
                  <div className="text-center py-12">
                    <MessageSquare className={`w-16 h-16 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'} mx-auto mb-4`} />
                    <h3 className={`text-xl font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-2`}>
                      No reviews yet
                    </h3>
                    <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} mb-6`}>
                      Be the first to share your experience!
                    </p>
                    {!user && (
                      <div className="flex justify-center space-x-4">
                        <button
                          onClick={() => navigate('/login')}
                          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <LogIn className="w-4 h-4 mr-2" />
                          Sign In to Review
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Review Form Modal */}
        {showReviewForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto`}>
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className={`text-2xl font-bold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
                    Write a Review
                  </h3>
                  <button
                    onClick={() => setShowReviewForm(false)}
                    className={`p-2 rounded-lg ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Error Message */}
                {submitStatus === 'error' && errorMessage && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                      <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                      <span className="text-red-800 font-medium">{errorMessage}</span>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmitReview} className="space-y-6">
                  {/* Rating */}
                  <div>
                    <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                      Rating *
                    </label>
                    <div className="flex items-center space-x-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => handleRatingChange(star)}
                          className="focus:outline-none"
                        >
                          <Star
                            className={`w-8 h-8 transition-colors ${
                              star <= formData.rating
                                ? 'text-yellow-400 fill-current'
                                : 'text-gray-300 hover:text-yellow-300'
                            }`}
                          />
                        </button>
                      ))}
                      <span className={`ml-2 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        ({formData.rating} star{formData.rating !== 1 ? 's' : ''})
                      </span>
                    </div>
                  </div>

                  {/* Title */}
                  <div>
                    <label htmlFor="title" className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                      Review Title *
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'} border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors`}
                      placeholder="Summarize your experience..."
                      required
                    />
                  </div>

                  {/* Project Type */}
                  <div>
                    <label htmlFor="projectType" className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                      Project Type
                    </label>
                    <select
                      id="projectType"
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors`}
                    >
                      <option value="">Select project type (optional)</option>
                      {projectTypes.map((type, index) => (
                        <option key={index} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  {/* Content */}
                  <div>
                    <label htmlFor="content" className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                      Your Review *
                    </label>
                    <textarea
                      id="content"
                      name="content"
                      value={formData.content}
                      onChange={handleInputChange}
                      rows={6}
                      className={`w-full px-4 py-3 ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'} border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none`}
                      placeholder="Share your detailed experience with AetherFlow. What did you like? How was the service? Any suggestions for improvement?"
                      required
                    />
                    <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                      {formData.content.length}/1000 characters (minimum 20)
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end space-x-4">
                    <button
                      type="button"
                      onClick={() => setShowReviewForm(false)}
                      className={`px-6 py-3 ${theme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} border rounded-lg font-medium transition-colors`}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className={`px-6 py-3 ${
                        submitting
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700'
                      } text-white rounded-lg font-medium transition-colors inline-flex items-center`}
                    >
                      {submitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Submit Review
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ReviewsSection;

